import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Bird } from 'lucide-react';
export function About() {
  return <section id="about" className="py-24 md:py-28 bg-soft-cream overflow-hidden relative" data-id="element-10" data-mp="2psibjdmyl9wk">
      
      {/* Delicate decorative blobs & icons */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-soft-terracotta/5 rounded-full mix-blend-multiply filter blur-3xl" data-id="element-11" data-mp="2tvevp8s5de2m"></div>
      <div className="absolute -bottom-20 left-10 w-80 h-80 bg-powder-blue/10 rounded-full mix-blend-multiply filter blur-3xl" data-id="element-12" data-mp="33rrm9xsebbt5"></div>
      <div className="absolute top-1/2 left-1/4 w-64 h-64 bg-soft-rose/10 rounded-full mix-blend-multiply filter blur-3xl" data-id="element-13" data-mp="38oruae8t2omw"></div>
      <div className="absolute top-1/3 right-1/4 w-48 h-48 bg-soft-butter/10 rounded-full mix-blend-multiply filter blur-3xl" data-id="element-14" data-mp="3djfalptnocqe"></div>

      <motion.div animate={{
      y: [0, -10, 0],
      rotate: [0, 5, 0]
    }} transition={{
      repeat: Infinity,
      duration: 7,
      ease: 'easeInOut'
    }} className="hidden md:block absolute top-24 left-1/2 text-[#FCD06D] z-0" data-id="element-15" data-mp="3mi9d1wto3xbv">
        
        <Bird className="w-10 h-10" strokeWidth={1} data-id="element-16" data-mp="2lp4gsp7lwoie" />
      </motion.div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-17" data-mp="2grcrm2ycjghw">
        <div className="flex flex-col md:flex-row items-center gap-10 md:gap-16" data-id="element-18" data-mp="2cq0xpl8fw93w">
          {/* Section heading — leads the section on mobile, sits in the text column on desktop */}
          <motion.div initial={{
          opacity: 0,
          y: 16
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          ease: 'easeOut'
        }} className="w-full space-y-6 md:hidden" data-id="element-19" data-mp="34hgu26zsta7b">
            
            <span className="inline-block text-xs font-medium uppercase tracking-[0.2em] border border-soft-butter/30 px-4 py-1.5 rounded-full text-[#75D5EA]" data-id="element-20" data-mp="2qr3lq3izvthg">
              Our Story
            </span>
            <h2 className="font-display text-4xl text-soft-terracotta leading-tight font-normal" data-id="element-21" data-mp="3jepml40u8k0m">
              It all started with <br data-id="element-22" data-mp="6751ch4o1m7k" />a little{' '}
              <span className="italic font-light" data-id="element-23" data-mp="67wxbt26dlyd">fairy.</span>
            </h2>
          </motion.div>

          {/* Image Side */}
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} className="w-full md:w-1/2 relative" data-id="element-24" data-mp="3tjfv5sqqymdt">
            
            <div className="relative z-10 rounded-t-full overflow-hidden border border-soft-terracotta/20 shadow-sm" data-id="element-25" data-mp="3okutdovujun9">
              <img src="https://cdn.magicpatterns.com/uploads/2NRxCNUZXfQbrefqzmUi6P/Screenshot_2026-06-08_at_3.06.29_PM.png" alt="Parents joyfully lifting their baby dressed as a fairy in a woodland setting" className="w-full h-[500px] md:h-auto object-cover object-top" data-id="element-26" data-mp="3almr2n4e06eu" />
              
            </div>
            {/* Delicate background shape */}
            <div className="absolute top-8 -left-6 w-full h-full rounded-t-full border border-powder-blue/30 z-0" data-id="element-27" data-mp="2xc1oto06em8n"></div>
          </motion.div>

          {/* Text Side */}
          <motion.div initial={{
          opacity: 0,
          x: 20
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: 'easeOut'
        }} className="w-full md:w-1/2 space-y-8" data-id="element-28" data-mp="2t8mxw2ap8lgf">
            
            <div className="hidden md:block space-y-8" data-id="element-29" data-mp="3c1qtht7h98do">
              <span className="inline-block text-xs font-medium uppercase tracking-[0.2em] border border-soft-butter/30 px-4 py-1.5 rounded-full text-[#75D5EA]" data-id="element-30" data-mp="3c3bm3da8eh8w">
                Our Story
              </span>
              <h2 className="font-display text-4xl md:text-5xl text-soft-terracotta leading-tight font-normal" data-id="element-31" data-mp="3l02tumy5bkjb">
                It all started with <br data-id="element-32" data-mp="1qoecyf8ct7wa" />a little{' '}
                <span className="italic font-light" data-id="element-33" data-mp="1qpfn8hhjpqcy">fairy.</span>
              </h2>
            </div>

            <div className="space-y-6 text-soft-dark font-light text-base leading-relaxed" data-id="element-34" data-mp="1t1zv2kxpe9c0">
              <p data-id="element-35" data-mp="1x2jtrsxxdl09">
                When my daughter turned one, I wanted to create something truly
                magical. I poured my heart into designing an enchanted fairy
                garden—hand-crafting mossy centerpieces, sourcing delicate
                linens, and curating every tiny detail.
              </p>
              <p data-id="element-36" data-mp="19ch6dq4yh37z">
                Seeing the wonder in her eyes, and the joy of our guests, I
                realized this was my passion. Friends began asking me to style
                their children's parties, and Wishing Tree Events was born.
              </p>
              <p data-id="element-37" data-mp="1o487u4807be5">
                Today, I bring that same level of love and meticulous attention
                to detail to every event I design. We believe that childhood is
                fleeting, and these milestones deserve to be celebrated
                beautifully.
              </p>
            </div>

            <div className="pt-8 border-t border-soft-terracotta/10" data-id="element-38" data-mp="o34hdos2s92g">
              <p className="font-display text-2xl text-soft-terracotta/80 font-normal italic" data-id="element-39" data-mp="j5l1yljn5q8x">
                "Creating magical celebrations and lasting memories for Santa
                Barbara families."
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}